import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import AppShell from "../components/AppShell";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { UserPlus, Copy, LinkSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLE_BADGE = {
  admin: "bg-[hsl(var(--accent))] text-white",
  project_manager: "bg-[hsl(var(--primary))] text-white",
  contractor: "bg-zinc-200 text-zinc-800",
};

const ROLE_LABEL = {
  admin: "Admin",
  project_manager: "Project Manager",
  contractor: "Contractor",
};

export default function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const canInvite = user?.role === "admin" || user?.role === "project_manager";

  const load = async () => {
    setLoading(true);
    try {
      const [u, i] = await Promise.all([
        api.get("/auth/users"),
        api.get("/team/invites").catch(() => ({ data: [] })),
      ]);
      setUsers(u.data);
      setInvites(i.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === "contractor") return;
    load();
  }, [user]);

  if (user?.role === "contractor") {
    return (
      <AppShell>
        <div className="ctrl-card p-12 text-center">
          <p className="font-display text-xl font-bold tracking-tight">No access.</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="space-y-10" data-testid="users-page">
        <header className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="label-eyebrow text-[#007BFF]">Team</span>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#001E44] mt-2">
              Users
            </h1>
            <p className="text-zinc-500 mt-2 text-sm sm:text-base">
              Everyone with access to this workspace. Account creation is by invite only.
            </p>
          </div>
          {canInvite && (
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  className="bg-[#007BFF] hover:bg-[#0062cc] rounded-full text-sm font-semibold px-5 py-2.5 h-auto shadow-sm hover:shadow-md transition-all"
                  data-testid="invite-teammate-button"
                >
                  <UserPlus size={14} weight="bold" className="mr-2" /> Invite teammate
                </Button>
              </DialogTrigger>
              <NewTeamInviteDialog
                currentRole={user.role}
                onCreated={load}
              />
            </Dialog>
          )}
        </header>

        {/* Users */}
        <section className="space-y-4">
          <div>
            <span className="label-eyebrow text-[#007BFF]">Members</span>
            <h2 className="font-display text-2xl font-semibold tracking-tight text-[#001E44] mt-1">Active accounts</h2>
          </div>
          {loading ? (
            <div className="ctrl-card h-40 bg-zinc-100 animate-pulse" />
          ) : (
            <div className="ctrl-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="ctrl-table min-w-[680px]" data-testid="users-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Company</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`user-row-${u.id}`}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-gradient-to-br from-[#001E44] to-[#1E407C] text-white rounded-full flex items-center justify-center text-xs font-semibold">
                            {u.name?.charAt(0)?.toUpperCase()}
                          </div>
                          <span className="font-semibold text-[#001E44]">{u.name}</span>
                        </div>
                      </td>
                      <td data-testid={`user-company-${u.id}`}>
                        {u.company ? (
                          <span className="text-sm">{u.company}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">—</span>
                        )}
                      </td>
                      <td className="font-mono text-xs">{u.email}</td>
                      <td>
                        <span className={`chip ${ROLE_BADGE[u.role] || "bg-zinc-100"}`}>
                          {u.role?.replace("_", " ")}
                        </span>
                      </td>
                      <td className="font-mono text-xs text-muted-foreground">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </section>

        {/* Pending invites */}
        {canInvite && invites.length > 0 && (
          <section className="space-y-4">
            <div>
              <span className="label-eyebrow text-[#007BFF]">Pending</span>
              <h2 className="font-display text-2xl font-semibold tracking-tight text-[#001E44] mt-1">Open invitation links</h2>
              <p className="text-sm text-zinc-500 mt-1">
                Share the link with the invitee. Each link can only be used once.
              </p>
            </div>
            <div className="ctrl-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="ctrl-table min-w-[760px]" data-testid="invites-table">
                <thead>
                  <tr>
                    <th>Role</th>
                    <th>Email</th>
                    <th>Company</th>
                    <th>Link</th>
                    <th>Expires</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map((inv) => (
                    <InviteRow key={inv.id} invite={inv} onChanged={load} />
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </section>
        )}
      </div>
    </AppShell>
  );
}

function InviteRow({ invite, onChanged }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(invite.invite_url);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed");
    }
  };
  const revoke = async () => {
    try {
      await api.delete(`/team/invites/${invite.id}`);
      toast.success("Invite revoked");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };
  return (
    <tr data-testid={`invite-row-${invite.id}`}>
      <td>
        <span className={`chip ${ROLE_BADGE[invite.role] || "bg-zinc-100"}`}>
          {ROLE_LABEL[invite.role] || invite.role}
        </span>
      </td>
      <td className="font-mono text-xs">{invite.email || "—"}</td>
      <td className="text-sm">{invite.company || "—"}</td>
      <td className="max-w-[280px]">
        <div className="flex gap-1 items-center">
          <input
            value={invite.invite_url} readOnly
            className="flex-1 text-[10px] font-mono px-2 py-1 bg-zinc-50 border border-border rounded-sm truncate"
            onFocus={(e) => e.target.select()}
            data-testid={`invite-url-${invite.id}`}
          />
          <button
            onClick={copy}
            className="p-1.5 border border-border rounded-sm hover:bg-zinc-50 transition-colors"
            data-testid={`copy-invite-row-${invite.id}`}
            title="Copy"
          >
            <Copy size={12} weight="bold" />
          </button>
        </div>
      </td>
      <td className="font-mono text-xs text-muted-foreground">
        {new Date(invite.expires_at).toLocaleDateString()}
      </td>
      <td className="text-right">
        <button
          onClick={revoke}
          className="text-zinc-400 hover:text-destructive transition-colors p-1"
          data-testid={`revoke-invite-${invite.id}`}
          title="Revoke"
        >
          <Trash size={14} weight="bold" />
        </button>
      </td>
    </tr>
  );
}

function NewTeamInviteDialog({ currentRole, onCreated }) {
  const [form, setForm] = useState({ role: "contractor", email: "", company: "" });
  const [busy, setBusy] = useState(false);
  const [createdUrl, setCreatedUrl] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [emailTo, setEmailTo] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { role: form.role, company: form.company };
      if (form.email) payload.email = form.email;
      const { data } = await api.post("/team/invites", payload);
      setCreatedUrl(data.invite_url);
      setEmailSent(!!data.email_sent);
      setEmailTo(data.email_to || data.email || null);
      if (data.email_sent) {
        toast.success(`Activation email sent to ${data.email_to || data.email}`);
      } else if (data.email) {
        toast.info("Couldn't send the email — share the link instead", { duration: 5000 });
      } else {
        toast.success("Invite link created");
      }
      onCreated?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(createdUrl);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  if (createdUrl) {
    return (
      <DialogContent className="sm:max-w-md" data-testid="new-invite-dialog-success">
        <DialogHeader>
          <DialogTitle className="font-display font-bold tracking-tight text-[#001E44]">
            {emailSent ? "Invitation sent" : "Invite ready"}
          </DialogTitle>
        </DialogHeader>
        {emailSent ? (
          <div
            className="rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-800 text-sm px-3 py-2.5"
            data-testid="invite-email-sent"
          >
            ✓ Activation email sent to <span className="font-semibold">{emailTo}</span>. They&apos;ll
            click the link in their inbox to set a password and join your workspace.
          </div>
        ) : (
          <p className="text-sm text-zinc-500">
            Share this link with your invitee. They&apos;ll set their name + password and join automatically.
          </p>
        )}
        <div className="flex gap-2 items-center mt-2">
          <input
            value={createdUrl} readOnly
            className="flex-1 text-xs font-mono px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg"
            onFocus={(e) => e.target.select()}
            data-testid="created-invite-url"
          />
          <Button
            type="button" onClick={copy}
            className="bg-[#007BFF] hover:bg-[#0062cc] rounded-full text-xs font-semibold px-4"
            data-testid="copy-created-invite"
          >
            <Copy size={12} weight="bold" className="mr-1.5" /> Copy
          </Button>
        </div>
      </DialogContent>
    );
  }

  return (
    <DialogContent className="sm:max-w-md" data-testid="new-invite-dialog">
      <DialogHeader>
        <DialogTitle className="font-display font-bold tracking-tight">Invite a teammate</DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Account type</Label>
          <Select
            value={form.role}
            onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}
          >
            <SelectTrigger className="rounded-md" data-testid="invite-role-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="contractor" data-testid="invite-role-contractor">Contractor</SelectItem>
              <SelectItem value="project_manager" data-testid="invite-role-pm">Project Manager</SelectItem>
              {currentRole === "admin" && (
                <SelectItem value="admin" data-testid="invite-role-admin">Administrator</SelectItem>
              )}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Choose the role <em>before</em> sharing — the link will be locked to that role.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Email (optional)</Label>
          <Input
            type="email" value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="invitee@company.com"
            data-testid="invite-email-input"
          />
          <p className="text-xs text-muted-foreground">
            Pre-fill the invitee&apos;s email. Leave blank to let them choose.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Company</Label>
          <Input
            value={form.company}
            onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
            placeholder="e.g. ACME Electrical"
            data-testid="invite-company-input"
          />
        </div>
        <DialogFooter className="pt-2">
          <Button
            type="submit" disabled={busy}
            className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 rounded-md text-xs uppercase tracking-wide font-semibold"
            data-testid="submit-new-invite"
          >
            <LinkSimple size={14} weight="bold" className="mr-2" />
            {busy ? "Generating..." : "Generate invite link"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
